import React from 'react';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import type { GlobalToken } from 'antd';
import { InfoCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Dash, HistoryEntry, Sparkline, renderStateWithStale, timeAgo } from './statusVisuals';
import type { HealthDetail, IPHealth, ProxyHealth, VantagePoint } from './healthDetailTypes';
import type { QuickCheckTarget } from './QuickCheckModal';
import type { HistoryModalData } from './CheckHistoryModal';

const { Text } = Typography;

// Column sets for the two HealthDetailPage tables. Both are budgeted to fit
// 1072px (rail expanded) with no horizontal scroll: every column but the
// address is pinned, the address takes the slack, numbers are right-aligned.

interface Handlers {
  token: GlobalToken;
  canWrite: boolean;
  onQuickCheck: (t: QuickCheckTarget) => void;
  onHistory: (d: HistoryModalData) => void;
}

// A column title with its explanation one hover away.
const hinted = (label: string, hint: string, token: GlobalToken) => (
  <Tooltip title={hint}>
    <Space size={2}>{label}<InfoCircleOutlined style={{ color: token.colorTextTertiary, fontSize: 12 }} /></Space>
  </Tooltip>
);

const small = (v: React.ReactNode) => <Text style={{ fontSize: 12 }}>{v}</Text>;

const workerCell = (r: VantagePoint) =>
  r.workerName ? (
    <Tooltip title={`${r.workerName}${r.workerIP ? ` · ${r.workerIP}` : ''}${r.workerCountry ? ` · ${r.workerCountry}` : ''}`}>
      <Text style={{ fontSize: 12 }} ellipsis>
        {r.workerCountry ? `${r.workerCountry} · ` : ''}{r.workerName}
      </Text>
    </Tooltip>
  ) : <Dash />;

const flagImg = (code: string) => (
  <img
    src={`https://flagcdn.com/20x15/${code.toLowerCase()}.png`}
    alt={code}
    width={16}
    height={12}
    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
  />
);

const QuickCheckButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Tooltip title="Quick check">
    <Button size="small" type="text" aria-label={`Quick check ${label}`} icon={<ThunderboltOutlined />} onClick={onClick} />
  </Tooltip>
);

const ms = (v: number, digits = 1) => (v > 0 ? `${v.toFixed(digits)} ms` : '--');

const common = (token: GlobalToken, onHistory: Handlers['onHistory']) => ({
  state: {
    title: 'State', dataIndex: 'state', key: 'state', width: 116,
    render: (state: string, r: IPHealth | ProxyHealth) => renderStateWithStale(state, r.staleSince),
  },
  checked: {
    title: 'Checked', dataIndex: 'checkedAt', key: 'checkedAt', width: 76, align: 'right' as const,
    render: (v: string) => (v ? <Text type="secondary" style={{ fontSize: 12 }}>{timeAgo(v)}</Text> : <Dash />),
  },
  worker: {
    title: hinted('Worker', 'Which worker ran the last check (vantage point recorded at check time)', token),
    key: 'worker', width: 104, ellipsis: true,
    render: (_: unknown, r: VantagePoint) => workerCell(r),
  },
  history: (title: (r: any) => string, target: (r: any) => string) => ({
    // 14 bars x 6px = 84px of strip plus cell padding.
    title: 'History', dataIndex: 'checksHistory', key: 'history', width: 104,
    render: (history: HistoryEntry[], r: any) => (
      <Sparkline history={history} onOpen={() => onHistory({ title: title(r), target: target(r), history })} />
    ),
  }),
});

export function buildIpColumns({ token, canWrite, onQuickCheck, onHistory }: Handlers) {
  const c = common(token, onHistory);
  return [
    { title: 'IP', dataIndex: 'ip', key: 'ip', ellipsis: true },
    c.state,
    {
      title: hinted('Latency', 'Round-trip time of ICMP ping to the server', token),
      dataIndex: 'latency', key: 'latency', width: 88, align: 'right' as const,
      render: (v: number) => small(ms(v)),
    },
    {
      title: hinted('PL', 'Packet loss — share of ICMP ping packets that got no response', token),
      dataIndex: 'packetLoss', key: 'packetLoss', width: 60, align: 'right' as const,
      render: (v: number) => (
        <Text style={{ fontSize: 12, color: v > 0 ? token.colorError : token.colorSuccess }}>{v.toFixed(0)}%</Text>
      ),
    },
    {
      title: hinted('Vis', 'Global visibility — share of worldwide probes that can reach this IP (Globalping)', token),
      dataIndex: 'visibility', key: 'visibility', width: 60, align: 'right' as const,
      render: (_: number, r: IPHealth) => {
        if (!r.visibilityChecked) {
          return <Tooltip title="Globalping data not yet available (pending enrichment)"><span><Dash /></span></Tooltip>;
        }
        const v = r.visibility;
        const color = v >= 75 ? token.colorSuccess : v >= 50 ? token.colorWarning : token.colorError;
        return <Text style={{ fontSize: 12, color }}>{v.toFixed(0)}%</Text>;
      },
    },
    {
      title: hinted('ASN', 'Autonomous System Number — the network operator hosting this IP', token),
      key: 'asn', width: 112, ellipsis: true,
      render: (_: unknown, r: IPHealth) => {
        if (r.asn) {
          const full = `AS${r.asn} ${r.asOrg || ''}`.trim();
          return <Tooltip title={full} placement="topLeft">{small(full)}</Tooltip>;
        }
        if (!r.visibilityChecked && !r.geoipChecked) {
          return <Tooltip title="ASN data not yet available (pending enrichment)"><span><Dash /></span></Tooltip>;
        }
        return <Dash />;
      },
    },
    c.checked,
    c.worker,
    c.history((r: IPHealth) => r.ip, (r: IPHealth) => r.ip),
    {
      // The four lookups used to be four identical globe icons; which one was
      // MTR was only discoverable by hovering each. They are now named links.
      title: 'Tools', key: 'tools', width: 176,
      render: (_: unknown, r: IPHealth) => {
        const tools = [
          { key: 'ping', label: 'Ping', url: `https://ping.pe/${r.ip}` },
          { key: 'mtr', label: 'MTR', url: `https://mtr.ping.pe/${r.ip}` },
          { key: 'dns', label: 'DNS', url: `https://dns.google/query?name=${r.ip}&type=PTR` },
          { key: 'bgp', label: 'BGP', url: `https://bgp.tools/prefix/${r.ip}` },
        ];
        return (
          <Space size={2}>
            {canWrite && (
              <QuickCheckButton label={r.ip} onClick={() => onQuickCheck({ target: r.ip, type: 'ip', proxy: null })} />
            )}
            {tools.map((t) => (
              <Tooltip key={t.key} title={`${t.label} ${r.ip} — opens in a new tab`}>
                <Button size="small" type="link" href={t.url} target="_blank" rel="noopener noreferrer" style={{ padding: '0 3px', fontSize: 12 }}>
                  {t.label}
                </Button>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
  ];
}

export function buildProxyColumns({ token, canWrite, onQuickCheck, onHistory, detail }: Handlers & { detail: HealthDetail }) {
  const c = common(token, onHistory);
  const isMobileGW = detail.type === 'mobile-gateway';
  const mobileCols = !isMobileGW ? [] : [
    {
      title: 'Modem', key: 'modem', width: 148, ellipsis: true,
      render: (_: unknown, r: ProxyHealth, index: number) => (
        <Tooltip title={`#${index + 1}${r.label ? ` — ${r.label}` : ''}${r.egressIP ? ` · egress ${r.egressIP}` : ''}`} placement="topLeft">
          <Space direction="vertical" size={0} style={{ lineHeight: 1.25 }}>
            <Text style={{ fontSize: 13 }}>#{index + 1}{r.label ? ` — ${r.label}` : ''}</Text>
            {r.egressIP && <Text type="secondary" style={{ fontSize: 12 }}>{r.egressIP}</Text>}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'Geo', key: 'geo', width: 96,
      render: (_: unknown, r: ProxyHealth) => {
        const gw = detail.country;
        const modem = r.country;
        const different = gw && modem && gw !== modem;
        return (
          <Tooltip title={`Gateway ${gw || '--'} → modem ${modem || '--'}`}>
            <Space size={4}>
              {gw ? flagImg(gw) : <Dash />}
              <Text type="secondary">→</Text>
              {modem ? flagImg(modem) : <Dash />}
              {different && (
                <Tooltip title="Modem is in a different region than the gateway; high latency may be expected.">
                  <Tag color="warning" style={{ fontSize: 12, lineHeight: '16px', padding: '0 4px', margin: 0 }}>XR</Tag>
                </Tooltip>
              )}
            </Space>
          </Tooltip>
        );
      },
    },
  ];
  return [
    ...mobileCols,
    {
      title: 'Proto', dataIndex: 'protocol', key: 'protocol', width: 84,
      render: (v: string) => <Tag style={{ margin: 0, fontSize: 12 }}>{(v || '').toUpperCase()}</Tag>,
    },
    {
      // On a relay every endpoint sits on the gateway's own address, so only
      // the port is printed for those; the full address stays in the tooltip.
      title: 'Host:Port', key: 'address', ellipsis: true,
      render: (_: unknown, r: ProxyHealth) => {
        const full = `${r.host}:${r.port}`;
        return <Tooltip title={full} placement="topLeft">{small(r.host === detail.mainIP ? `:${r.port}` : full)}</Tooltip>;
      },
    },
    c.state,
    {
      title: hinted('Latency', 'Round-trip time of the HTTP request through the proxy', token),
      dataIndex: 'latency', key: 'latency', width: 88, align: 'right' as const,
      render: (v: number) => small(ms(v)),
    },
    {
      title: hinted('Total', 'Total time for all check steps (connect + request + latency eval)', token),
      dataIndex: 'totalDuration', key: 'totalDuration', width: 80, align: 'right' as const,
      render: (v: number) => small(ms(v, 0)),
    },
    c.checked,
    c.worker,
    c.history(
      (r: ProxyHealth) => `${(r.protocol || '').toUpperCase()} ${r.host}:${r.port}${r.label ? ' — ' + r.label : ''}`,
      (r: ProxyHealth) => `${r.host}:${r.port}`,
    ),
    ...(canWrite ? [{
      title: 'Check', key: 'tools', width: 64, align: 'center' as const,
      render: (_: unknown, r: ProxyHealth) => (
        <QuickCheckButton
          label={`${r.host}:${r.port}`}
          onClick={() => onQuickCheck({ target: `${r.host}:${r.port}`, type: 'proxy', proxy: { host: r.host, port: r.port } })}
        />
      ),
    }] : []),
  ];
}
